import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Container, Flex, Image, Spinner, Tabs, TabList, TabPanels, Tab, TabPanel, Text, IconButton, } from "@chakra-ui/react";
import { useEffect, useState, useRef, useCallback } from "react";
import { usePubMedia } from "../hooks/usePubMedia";
import { usePub } from "../hooks/usePub";
import BackNavigation from "../components/BackNavigation";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon, ArrowBackIcon, } from "@chakra-ui/icons";
import { appendTrackingParams } from "../shared-utils/trackingUtils";
import { ZILLOUT_WEB_BASE_URL } from "../config/urls";
const PhotoGallery = ({ appContext, pubId }) => {
    const params = useParams();
    const pub_id = appContext === "experience" ? pubId : params.pubId;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { data: pub, isLoading: isPubLoading } = usePub(pub_id);
    const { data: mediaData, isLoading: isMediaLoading } = usePubMedia(pub_id);
    const [images, setImages] = useState([]);
    const [vibes, setVibes] = useState([]);
    const [hasVibes, setHasVibes] = useState(false);
    const [tableLayout, setTableLayout] = useState([]);
    // State management for full-screen gallery - track index per category
    const [currentCategory, setCurrentCategory] = useState(0);
    const [categoryIndices, setCategoryIndices] = useState({
        0: 0,
        1: 0,
        2: 0, // Table Layout
    });
    const [isVideoMuted, setIsVideoMuted] = useState(true);
    const mainVideoRef = useRef(null);
    const thumbnailContainerRef = useRef(null);
    // Swipe gesture state - improved for fluid gestures
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchEndX = useRef(0);
    const touchStartTime = useRef(0);
    const isSwiping = useRef(false);
    const swipeOffset = useRef(0); // Current swipe offset for fluid animation
    const swipeThreshold = 30; // Minimum distance for swipe
    const swipeVelocityThreshold = 0.3; // Minimum velocity (px/ms)
    const [swipeTranslateX, setSwipeTranslateX] = useState(0); // For fluid swipe animation
    const [isAnimating, setIsAnimating] = useState(false); // Track if we're animating
    const [isCurrentlySwiping, setIsCurrentlySwiping] = useState(false); // Track swipe state for will-change
    const animationFrameRef = useRef(null); // For smooth animation updates
    const swipeContainerRef = useRef(null); // Direct DOM reference for performance
    const currentTransformRef = useRef(0); // Track current transform for smooth updates
    const [dataFetched, setDataFetched] = useState(false);
    useEffect(() => {
        var _a, _b;
        if (!isMediaLoading && mediaData && mediaData.data) {
            setDataFetched(true);
            // Set pub images
            const pubImagesArray = ((_a = mediaData.data.pubImages) === null || _a === void 0 ? void 0 : _a.images_url) || [];
            setImages(pubImagesArray);
            // Set vibes
            const pubVibes = ((_b = mediaData.data.vibes) === null || _b === void 0 ? void 0 : _b.vibes) || [];
            if (pubVibes.length > 0) {
                setVibes(pubVibes);
                setHasVibes(true);
            }
            // Combine tableLayout and tableSectionImages
            const combinedTableLayout = [];
            // Add table layout items
            if (mediaData.data.tableLayout && mediaData.data.tableLayout.length > 0) {
                mediaData.data.tableLayout.forEach((item) => {
                    combinedTableLayout.push({
                        imageUrl: item.imageUrl,
                        floorName: item.floorName,
                    });
                });
            }
            // Add table section images
            if (mediaData.data.tableSectionImages &&
                mediaData.data.tableSectionImages.length > 0) {
                mediaData.data.tableSectionImages.forEach((section) => {
                    // Only add if images array exists and is not null
                    if (section.images && Array.isArray(section.images)) {
                        section.images.forEach((imageUrl) => {
                            combinedTableLayout.push({
                                imageUrl,
                                areaName: section.areaName,
                            });
                        });
                    }
                });
            }
            setTableLayout(combinedTableLayout);
        }
    }, [mediaData]);
    // Initialize from URL params
    useEffect(() => {
        if (dataFetched) {
            const section = searchParams.get("section");
            const urlParam = searchParams.get("url");
            if (section) {
                let tabIndex = -1;
                let itemIndex = -1;
                if (section === "chillout" && hasVibes) {
                    tabIndex = 0;
                    // If URL is provided, find the index by matching the URL
                    if (urlParam) {
                        const decodedUrl = decodeURIComponent(urlParam);
                        itemIndex = vibes.findIndex((url) => url === decodedUrl);
                    }
                }
                else if (section === "table" && tableLayout.length > 0) {
                    tabIndex = hasVibes ? 1 : 0;
                    // If URL is provided, find the index by matching the URL
                    if (urlParam) {
                        const decodedUrl = decodeURIComponent(urlParam);
                        itemIndex = tableLayout.findIndex((item) => item.imageUrl === decodedUrl);
                    }
                }
                else if (section === "venue") {
                    if (hasVibes && tableLayout.length > 0) {
                        tabIndex = 2;
                    }
                    else if (hasVibes || tableLayout.length > 0) {
                        tabIndex = 1;
                    }
                    else {
                        tabIndex = 0;
                    }
                    // If URL is provided, find the index by matching the URL
                    if (urlParam) {
                        const decodedUrl = decodeURIComponent(urlParam);
                        itemIndex = images.findIndex((url) => url === decodedUrl);
                    }
                }
                if (tabIndex >= 0) {
                    setCurrentCategory(tabIndex);
                    // Only set item index if URL was provided and found
                    if (urlParam && itemIndex >= 0) {
                        setCategoryIndices((prev) => ({
                            ...prev,
                            [tabIndex]: itemIndex,
                        }));
                    }
                    // Clear URL params after initialization
                }
            }
        }
    }, [
        dataFetched,
        hasVibes,
        tableLayout,
        images,
        searchParams,
        setSearchParams,
        vibes,
    ]);
    // Reset video mute state and swipe state when category changes
    useEffect(() => {
        setIsVideoMuted(true);
        setSwipeTranslateX(0);
        setIsAnimating(false);
        setIsCurrentlySwiping(false);
        // Cancel any pending animation frames
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, [currentCategory]);
    // Cleanup animation frames on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);
    // Get current index for active category
    const getCurrentIndex = () => categoryIndices[currentCategory] || 0;
    const scrollThumbnailIntoView = useCallback((index) => {
        const key = `${currentCategory}-${index}`;
        // Find thumbnail by data attribute - search in the document
        const thumbnail = document.querySelector(`[data-thumbnail-key="${key}"]`);
        if (!thumbnail)
            return;
        // Find the scrollable parent container
        let container = thumbnail.parentElement;
        while (container) {
            const style = window.getComputedStyle(container);
            if (container.scrollWidth > container.clientWidth &&
                (style.overflowX === "auto" ||
                    style.overflowX === "scroll" ||
                    style.overflow === "auto" ||
                    style.overflow === "scroll")) {
                break;
            }
            container = container.parentElement;
        }
        if (!container)
            return;
        // Calculate scroll position to center the thumbnail
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const thumbnailLeft = thumbnailRect.left - containerRect.left + container.scrollLeft;
        const thumbnailCenter = thumbnailLeft + thumbnailRect.width / 2;
        const containerCenter = container.clientWidth / 2;
        const targetScroll = thumbnailCenter - containerCenter;
        // Clamp to valid scroll range
        const maxScroll = container.scrollWidth - container.clientWidth;
        const clampedScroll = Math.max(0, Math.min(targetScroll, maxScroll));
        container.scrollTo({
            left: clampedScroll,
            behavior: "smooth",
        });
    }, [currentCategory]);
    // Auto-scroll thumbnail when index or category changes
    useEffect(() => {
        const currentIdx = categoryIndices[currentCategory] || 0;
        const timeoutId = setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    scrollThumbnailIntoView(currentIdx);
                });
            });
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [categoryIndices, currentCategory, scrollThumbnailIntoView]);
    const isVideo = (url) => {
        if (!url)
            return false;
        const baseUrl = url.split("?")[0];
        return /(0?mp4|0?mov|0?webm|\.mp4|\.mov|\.webm|\.avi)$/i.test(baseUrl);
    };
    const setCurrentIndex = (index) => {
        setCategoryIndices((prev) => ({
            ...prev,
            [currentCategory]: index,
        }));
    };
    // Get total number of tabs based on available content
    const getTotalTabs = () => {
        let count = 1; // Venue Images is always present
        if (hasVibes)
            count++;
        if (tableLayout.length > 0)
            count++;
        return count;
    };
    // Get items for a specific tab index
    const getItemsForTab = (tabIndex) => {
        // Tab order: Chillout Moments (if hasVibes), Table Layout (if tableLayout.length > 0), Venue Images (always)
        let currentTab = 0;
        if (hasVibes) {
            if (tabIndex === currentTab) {
                return vibes.map((src) => ({
                    src,
                    type: isVideo(src) ? "video" : "image",
                }));
            }
            currentTab++;
        }
        if (tableLayout.length > 0) {
            if (tabIndex === currentTab) {
                return tableLayout.map((item) => ({
                    src: item.imageUrl,
                    type: isVideo(item.imageUrl) ? "video" : "image",
                    floorName: item.floorName,
                    areaName: item.areaName,
                }));
            }
            currentTab++;
        }
        // Venue Images (always last)
        return images.map((src) => ({
            src,
            type: isVideo(src) ? "video" : "image",
        }));
    };
    const handleNext = (items) => {
        const totalItems = items.length;
        if (totalItems === 0)
            return;
        const currentIdx = getCurrentIndex();
        const totalTabs = getTotalTabs();
        // Check if we're at the last item and can switch to next tab
        if (currentIdx === totalItems - 1 && currentCategory < totalTabs - 1) {
            // Switch to next tab and start at first item
            const nextTab = currentCategory + 1;
            setCategoryIndices((prev) => ({
                ...prev,
                [nextTab]: 0,
            }));
            setCurrentCategory(nextTab);
            setIsVideoMuted(true);
            setSwipeTranslateX(0);
            setIsAnimating(false);
            setIsCurrentlySwiping(false);
            return;
        }
        const nextIndex = (currentIdx + 1) % totalItems;
        setCurrentIndex(nextIndex);
        setIsVideoMuted(true); // Reset mute state when navigating
        setSwipeTranslateX(0); // Reset swipe position
        setIsAnimating(false); // Reset animation state
        setIsCurrentlySwiping(false); // Reset swipe state
    };
    const handlePrevious = (items) => {
        const totalItems = items.length;
        if (totalItems === 0)
            return;
        const currentIdx = getCurrentIndex();
        // Check if we're at the first item and can switch to previous tab
        if (currentIdx === 0 && currentCategory > 0) {
            // Switch to previous tab and start at last item
            const prevTab = currentCategory - 1;
            const prevTabItems = getItemsForTab(prevTab);
            setCategoryIndices((prev) => ({
                ...prev,
                [prevTab]: prevTabItems.length - 1,
            }));
            setCurrentCategory(prevTab);
            setIsVideoMuted(true);
            setSwipeTranslateX(0);
            setIsAnimating(false);
            setIsCurrentlySwiping(false);
            return;
        }
        const prevIndex = (currentIdx - 1 + totalItems) % totalItems;
        setCurrentIndex(prevIndex);
        setIsVideoMuted(true); // Reset mute state when navigating
        setSwipeTranslateX(0); // Reset swipe position
        setIsAnimating(false); // Reset animation state
        setIsCurrentlySwiping(false); // Reset swipe state
    };
    const handleThumbnailClick = (index) => {
        setCurrentIndex(index);
        setIsVideoMuted(true);
        setSwipeTranslateX(0); // Reset swipe position
        setIsAnimating(false); // Reset animation state
        setIsCurrentlySwiping(false); // Reset swipe state
    };
    const handleVideoToggleMute = () => {
        if (mainVideoRef.current) {
            mainVideoRef.current.muted = !mainVideoRef.current.muted;
            setIsVideoMuted(mainVideoRef.current.muted);
        }
    };
    const isLoading = isPubLoading || isMediaLoading;
    if (isLoading) {
        return (_jsxs(Container, { maxW: "100vw", minH: "100vh", background: "#111111", p: "0", children: [_jsx(BackNavigation, { heading: "Loading..." }), _jsx(Flex, { w: "100%", h: "75vh", alignItems: "center", justifyContent: "center", children: _jsx(Spinner, { color: "white" }) })] }));
    }
    // Render gallery content component
    const renderGalleryContent = (items) => {
        const itemCount = items.length;
        const currentIdx = getCurrentIndex();
        const currentItem = items[currentIdx];
        if (itemCount === 0) {
            return (_jsx(Flex, { flex: 1, alignItems: "center", justifyContent: "center", children: _jsx(Text, { color: "white", children: "No items available" }) }));
        }
        const handleSwipeStart = (e) => {
            const touch = e.touches[0];
            touchStartX.current = touch.clientX;
            touchStartY.current = touch.clientY;
            touchStartTime.current = Date.now();
            isSwiping.current = false;
            touchEndX.current = touch.clientX; // Initialize end position
            swipeOffset.current = 0;
            currentTransformRef.current = 0;
            setSwipeTranslateX(0);
            setIsAnimating(false);
            setIsCurrentlySwiping(false);
            // Cancel any pending animation frame
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
        const handleSwipeMove = (e) => {
            if (!touchStartX.current)
                return;
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX.current;
            const deltaY = Math.abs(touch.clientY - touchStartY.current);
            const absDeltaX = Math.abs(deltaX);
            // Only consider horizontal swipes (ignore vertical scrolling)
            // Require minimum horizontal movement to start swiping
            if (absDeltaX > 5 && absDeltaX > deltaY * 1.5) {
                if (!isSwiping.current) {
                    isSwiping.current = true;
                    setIsCurrentlySwiping(true);
                    // Prevent default scrolling when swiping horizontally
                    e.preventDefault();
                }
                else {
                    // Continue preventing default during swipe
                    e.preventDefault();
                }
                // Update swipe offset for fluid animation
                swipeOffset.current = deltaX;
                currentTransformRef.current = deltaX;
                // Cancel any pending frame
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                // Update state immediately for smooth React updates
                setSwipeTranslateX(deltaX);
                // Also update DOM directly in RAF for maximum smoothness
                animationFrameRef.current = requestAnimationFrame(() => {
                    if (swipeContainerRef.current && isSwiping.current) {
                        // Ensure no transition during active swipe
                        swipeContainerRef.current.style.transition = "none";
                        // Image should move WITH the finger direction
                        swipeContainerRef.current.style.transform = `translateX(${deltaX}px)`;
                    }
                    animationFrameRef.current = null;
                });
            }
            touchEndX.current = touch.clientX;
        };
        const handleSwipeEnd = () => {
            // Cancel any pending animation frame
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            setIsCurrentlySwiping(false);
            if (!touchStartX.current || touchEndX.current === undefined) {
                touchStartX.current = 0;
                touchEndX.current = 0;
                isSwiping.current = false;
                swipeOffset.current = 0;
                currentTransformRef.current = 0;
                setSwipeTranslateX(0);
                setIsAnimating(false);
                return;
            }
            if (!isSwiping.current) {
                touchStartX.current = 0;
                touchEndX.current = 0;
                swipeOffset.current = 0;
                currentTransformRef.current = 0;
                setSwipeTranslateX(0);
                setIsAnimating(false);
                return;
            }
            const distance = touchStartX.current - touchEndX.current;
            const timeElapsed = Date.now() - touchStartTime.current;
            const velocity = timeElapsed > 0 ? Math.abs(distance) / timeElapsed : 0;
            // Check if swipe meets threshold (distance or velocity)
            // Lower threshold for faster swipes (velocity-based)
            const effectiveThreshold = velocity > swipeVelocityThreshold
                ? swipeThreshold * 0.5
                : swipeThreshold;
            // Get current position for smooth transition
            const currentPosition = swipeTranslateX;
            const windowWidth = window.innerWidth;
            // Use requestAnimationFrame to ensure smooth transition start
            requestAnimationFrame(() => {
                // Enable animation AFTER ensuring current position is set
                setIsAnimating(true);
                // Animate to final position
                if (Math.abs(distance) > effectiveThreshold ||
                    velocity > swipeVelocityThreshold) {
                    // Animate transition to next/previous
                    // Note: distance = touchStartX - touchEndX
                    // If distance > 0: finger moved left (swipe left) -> show next
                    // If distance < 0: finger moved right (swipe right) -> show previous
                    // Animate current item off screen in the direction of swipe
                    const targetPosition = distance > 0 ? -windowWidth : windowWidth;
                    setSwipeTranslateX(targetPosition);
                    // After animation, change the index
                    setTimeout(() => {
                        if (distance > 0) {
                            // Swipe left (finger moved left) - next
                            handleNext(items);
                        }
                        else {
                            // Swipe right (finger moved right) - previous
                            handlePrevious(items);
                        }
                        // Reset position
                        setSwipeTranslateX(0);
                        setIsAnimating(false);
                    }, 350);
                }
                else {
                    // Snap back to center if threshold not met
                    setSwipeTranslateX(0);
                    setTimeout(() => {
                        setIsAnimating(false);
                    }, 350);
                }
            });
            // Reset touch state
            touchStartX.current = 0;
            touchEndX.current = 0;
            isSwiping.current = false;
            swipeOffset.current = 0;
            currentTransformRef.current = 0;
        };
        // Get previous and next items for fluid swipe (with cross-section awareness)
        const getPreviousItem = () => {
            if (itemCount === 0)
                return null;
            // If at first item and can switch to previous tab, show last item of previous tab
            if (currentIdx === 0 && currentCategory > 0) {
                const prevTabItems = getItemsForTab(currentCategory - 1);
                if (prevTabItems.length > 0) {
                    return prevTabItems[prevTabItems.length - 1];
                }
            }
            const prevIndex = (currentIdx - 1 + itemCount) % itemCount;
            return items[prevIndex];
        };
        const getNextItem = () => {
            if (itemCount === 0)
                return null;
            const totalTabs = getTotalTabs();
            // If at last item and can switch to next tab, show first item of next tab
            if (currentIdx === itemCount - 1 && currentCategory < totalTabs - 1) {
                const nextTabItems = getItemsForTab(currentCategory + 1);
                if (nextTabItems.length > 0) {
                    return nextTabItems[0];
                }
            }
            const nextIndex = (currentIdx + 1) % itemCount;
            return items[nextIndex];
        };
        const previousItem = getPreviousItem();
        const nextItem = getNextItem();
        return (_jsxs(_Fragment, { children: [_jsxs(Box, { position: "relative", flex: 1, w: "100%", minH: 0, display: "flex", alignItems: "center", justifyContent: "center", onTouchStart: handleSwipeStart, onTouchMove: handleSwipeMove, onTouchEnd: handleSwipeEnd, onTouchCancel: handleSwipeEnd, overflow: "hidden", style: {
                        touchAction: "pan-y",
                        WebkitOverflowScrolling: "touch",
                    }, children: [itemCount > 1 && (_jsx(IconButton, { "aria-label": "Previous", icon: _jsx(ChevronLeftIcon, {}), display: ["none", "none", "block"], onClick: () => handlePrevious(items), position: "absolute", left: [2, 4, 6], zIndex: 10, bg: "rgba(0,0,0,0.6)", color: "white", fontSize: ["2xl", "3xl", "4xl"], width: ["40px", "50px", "60px"], height: ["40px", "50px", "60px"], borderRadius: "full", _hover: { bg: "rgba(0,0,0,0.8)" } })), _jsxs(Box, { w: "100%", h: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden", children: [previousItem && itemCount > 1 && (_jsx(Box, { position: "absolute", left: 0, top: 0, w: "100%", h: "100%", display: "flex", alignItems: "center", justifyContent: "center", style: {
                                        transform: `translateX(calc(-100% + ${swipeTranslateX > 0 ? swipeTranslateX : 0}px))`,
                                        transition: isAnimating
                                            ? "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                                            : "none",
                                        willChange: isCurrentlySwiping || isAnimating ? "transform" : "auto",
                                        zIndex: swipeTranslateX > 0 ? 2 : 1,
                                    }, children: previousItem.type === "video" ? (_jsx(Box, { as: "video", src: previousItem.src, w: "100%", h: "100%", objectFit: "contain", muted: true, loop: true, playsInline: true, style: { maxHeight: "100%" } })) : (_jsx(Image, { src: previousItem.src, alt: previousItem.floorName ||
                                            previousItem.areaName ||
                                            `Gallery item ${((currentIdx - 1 + itemCount) % itemCount) + 1}`, w: "100%", h: "100%", objectFit: "contain", loading: "lazy" })) })), currentItem && (_jsxs(Box, { ref: swipeContainerRef, position: "absolute", left: 0, top: 0, w: "100%", h: "100%", display: "flex", alignItems: "center", justifyContent: "center", style: {
                                        transform: `translateX(${swipeTranslateX}px)`,
                                        transition: isAnimating
                                            ? "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                                            : "none",
                                        willChange: isCurrentlySwiping || isAnimating ? "transform" : "auto",
                                        zIndex: 2,
                                    }, children: [currentItem.type === "video" ? (_jsx(Box, { as: "video", ref: mainVideoRef, src: currentItem.src, w: "100%", h: "100%", objectFit: "contain", autoPlay: true, loop: true, muted: isVideoMuted, playsInline: true, style: { maxHeight: "100%" } })) : (_jsx(Image, { src: currentItem.src, alt: currentItem.floorName ||
                                                currentItem.areaName ||
                                                `Gallery item ${currentIdx + 1}`, w: "100%", h: "100%", objectFit: "contain", loading: "eager" })), currentItem.type === "video" && (_jsx(Box, { position: "absolute", bottom: 4, right: 4, w: "36px", h: "36px", borderRadius: "full", bg: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", onClick: handleVideoToggleMute, transition: "all 0.2s ease", backdropFilter: "blur(8px)", _hover: { bg: "rgba(0,0,0,0.8)" }, children: isVideoMuted ? (_jsx(Box, { as: "svg", width: "18px", height: "18px", viewBox: "0 0 24 24", fill: "white", children: _jsx("path", { d: "M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" }) })) : (_jsx(Box, { as: "svg", width: "18px", height: "18px", viewBox: "0 0 24 24", fill: "white", children: _jsx("path", { d: "M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" }) })) }))] })), nextItem && itemCount > 1 && (_jsx(Box, { position: "absolute", right: 0, top: 0, w: "100%", h: "100%", display: "flex", alignItems: "center", justifyContent: "center", style: {
                                        transform: `translateX(calc(100% + ${swipeTranslateX < 0 ? swipeTranslateX : 0}px))`,
                                        transition: isAnimating
                                            ? "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                                            : "none",
                                        willChange: isCurrentlySwiping || isAnimating ? "transform" : "auto",
                                        zIndex: swipeTranslateX < 0 ? 2 : 1,
                                    }, children: nextItem.type === "video" ? (_jsx(Box, { as: "video", src: nextItem.src, w: "100%", h: "100%", objectFit: "contain", muted: true, loop: true, playsInline: true, style: { maxHeight: "100%" } })) : (_jsx(Image, { src: nextItem.src, alt: nextItem.floorName ||
                                            nextItem.areaName ||
                                            `Gallery item ${((currentIdx + 1) % itemCount) + 1}`, w: "100%", h: "100%", objectFit: "contain", loading: "lazy" })) }))] }), itemCount > 1 && (_jsx(IconButton, { "aria-label": "Next", icon: _jsx(ChevronRightIcon, {}), display: ["none", "none", "block"], onClick: () => handleNext(items), position: "absolute", right: [2, 4, 6], zIndex: 10, bg: "rgba(0,0,0,0.6)", color: "white", fontSize: ["2xl", "3xl", "4xl"], width: ["40px", "50px", "60px"], height: ["40px", "50px", "60px"], borderRadius: "full", _hover: { bg: "rgba(0,0,0,0.8)" } })), (() => {
                            // Get unique area names with their first index
                            const areaNameMap = new Map();
                            items.forEach((item, index) => {
                                if (item.areaName && !areaNameMap.has(item.areaName)) {
                                    areaNameMap.set(item.areaName, index);
                                }
                            });
                            const uniqueAreas = Array.from(areaNameMap.entries());
                            if (uniqueAreas.length === 0)
                                return null;
                            // Auto-scroll active area into view
                            const activeAreaName = currentItem === null || currentItem === void 0 ? void 0 : currentItem.areaName;
                            if (activeAreaName) {
                                setTimeout(() => {
                                    const activeEl = document.querySelector(`[data-area-name="${activeAreaName}"]`);
                                    if (activeEl) {
                                        activeEl.scrollIntoView({
                                            behavior: "smooth",
                                            block: "nearest",
                                            inline: "center",
                                        });
                                    }
                                }, 100);
                            }
                            return (_jsx(Box, { position: "absolute", bottom: 3, left: 0, right: 0, zIndex: 15, pointerEvents: "none", children: _jsx(Flex, { gap: 2, overflowX: "auto", px: 4, py: 2, justifyContent: uniqueAreas.length <= 3 ? "center" : "flex-start", sx: {
                                        "&::-webkit-scrollbar": { display: "none" },
                                        scrollbarWidth: "none",
                                        msOverflowStyle: "none",
                                    }, pointerEvents: "auto", children: uniqueAreas.map(([areaName, firstIndex]) => {
                                        const isActive = (currentItem === null || currentItem === void 0 ? void 0 : currentItem.areaName) === areaName;
                                        return (_jsx(Box, { "data-area-name": areaName, flexShrink: 0, px: 3, py: 1.5, borderRadius: "full", bg: isActive
                                                ? "rgba(255,255,255,0.95)"
                                                : "rgba(0,0,0,0.5)", color: isActive ? "#111" : "white", fontSize: "xs", fontWeight: isActive ? 600 : 500, cursor: "pointer", transition: "all 0.2s ease", backdropFilter: "blur(12px)", boxShadow: "0 2px 12px rgba(0,0,0,0.3)", _hover: {
                                                bg: isActive
                                                    ? "rgba(255,255,255,1)"
                                                    : "rgba(0,0,0,0.7)",
                                                transform: "scale(1.02)",
                                            }, onClick: () => handleThumbnailClick(firstIndex), whiteSpace: "nowrap", children: areaName }, areaName));
                                    }) }) }));
                        })()] }), itemCount > 0 && (_jsxs(Flex, { justifyContent: "center", alignItems: "center", gap: 1, pt: 2, pb: 1, color: "white", fontSize: ["md", "lg"], flexShrink: 0, children: [_jsx(ChevronLeftIcon, { boxSize: 6, onClick: () => handlePrevious(items), _disabled: {
                                color: "rgba(255,255,255,0.5)",
                                cursor: "not-allowed",
                            } }), _jsxs(Text, { fontWeight: 600, children: [currentIdx + 1, " ", _jsxs("span", { style: {
                                        color: "#9D9D9D",
                                        fontSize: "12px",
                                        fontWeight: "400",
                                    }, children: ["/ ", itemCount] })] }), _jsx(ChevronRightIcon, { boxSize: 6, onClick: () => handleNext(items), _disabled: {
                                color: "rgba(255,255,255,0.5)",
                                cursor: "not-allowed",
                            } })] })), itemCount > 0 && (_jsx(Box, { px: [0, 0, 4], pb: 8, flexShrink: 0, children: _jsx(Flex, { ref: thumbnailContainerRef, gap: 1, overflowX: "auto", overflowY: "hidden", py: 2, px: 2, minH: ["90px", "96px", "110px"], alignItems: "center", justifyContent: ["flex-start", "flex-start", "center"], sx: {
                            "&::-webkit-scrollbar": {
                                height: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "rgba(255,255,255,0.1)",
                                borderRadius: "2px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "rgba(255,255,255,0.3)",
                                borderRadius: "2px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                background: "rgba(255,255,255,0.5)",
                            },
                        }, children: items.map((item, index) => {
                            const isSelected = index === currentIdx;
                            const thumbnailKey = `${currentCategory}-${index}`;
                            const prevItem = index > 0 ? items[index - 1] : null;
                            const isNewArea = item.areaName &&
                                (!prevItem || prevItem.areaName !== item.areaName);
                            return (_jsxs(Box, { display: "flex", alignItems: "center", flexShrink: 0, children: [isNewArea && (_jsxs(Flex, { flexDirection: "column", alignItems: "center", justifyContent: "center", px: 2, gap: 1, flexShrink: 0, children: [_jsx(Box, { w: "1px", h: "20px", bg: "rgba(255,255,255,0.3)" }), _jsx(Text, { fontSize: "9px", color: "rgba(255,255,255,0.7)", fontWeight: 500, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.5px", maxW: "60px", textAlign: "center", noOfLines: 2, children: item.areaName }), _jsx(Box, { w: "1px", h: "20px", bg: "rgba(255,255,255,0.3)" })] })), _jsx(Box, { "data-thumbnail-key": thumbnailKey, flexShrink: 0, w: isSelected
                                            ? ["74px", "80px", "90px"]
                                            : ["64px", "70px", "80px"], h: isSelected
                                            ? ["74px", "80px", "90px"]
                                            : ["64px", "70px", "80px"], borderRadius: "8px", overflow: "hidden", cursor: "pointer", border: isSelected ? "2px solid white" : "2px solid transparent", transition: "all 0.2s", onClick: () => handleThumbnailClick(index), position: "relative", opacity: isSelected ? 1 : 0.9, children: item.type === "video" ? (_jsxs(Box, { position: "relative", w: "100%", h: "100%", children: [_jsx(Box, { as: "video", src: item.src, w: "100%", h: "100%", objectFit: "cover", muted: true, loop: true, playsInline: true, preload: "metadata" }), _jsx(Box, { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", w: "24px", h: "24px", borderRadius: "full", bg: "rgba(0, 0, 0, 0.6)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", children: _jsx(Box, { as: "svg", width: "12px", height: "12px", viewBox: "0 0 24 24", fill: "white", ml: "2px", children: _jsx("path", { d: "M8 5v14l11-7z" }) }) })] })) : (_jsx(Image, { src: item.src, alt: item.floorName ||
                                                item.areaName ||
                                                `Thumbnail ${index + 1}`, w: "100%", h: "100%", objectFit: "cover", loading: "lazy" })) })] }, thumbnailKey));
                        }) }) }))] }));
    };
    const goBack = () => {
        if (appContext === "experience") {
            if (!window.history || window.history.length <= 1) {
                window.open(appendTrackingParams(`${ZILLOUT_WEB_BASE_URL}/home`), "_self");
            }
            else {
                navigate(-1);
            }
        }
        else {
            if (!window.history || window.history.length <= 1) {
                navigate("/home");
            }
            else {
                navigate(-1);
            }
        }
    };
    return (_jsxs(Container, { maxW: "100vw", minH: "100vh", background: "#111111", p: "0", children: [_jsx(Box, { display: ["block", "block", "none"], children: _jsx(BackNavigation, { heading: "Gallery", subHeading: pub && pub.data && pub.data.pub_detail.name }) }), _jsxs(Flex, { display: ["none", "none", "flex"], alignItems: "center", px: 8, py: 6, gap: 4, children: [_jsx(ArrowBackIcon, { color: "white", fontSize: "2xl", cursor: "pointer", onClick: goBack }), _jsxs(Flex, { flexDirection: "column", gap: 1, children: [_jsx(Text, { color: "white", fontSize: "lg", fontWeight: 600, children: "Gallery" }), pub && pub.data && pub.data.pub_detail.name && (_jsx(Text, { color: "#9D9D9D", fontSize: "sm", fontWeight: 400, children: pub.data.pub_detail.name }))] })] }), _jsx(Box, { pt: [20, 20, 0], w: "100%", h: ["100vh", "100vh", "calc(100vh - 80px)"], display: "flex", flexDirection: "column", overflowY: "hidden", children: _jsxs(Tabs, { variant: "solid-rounded", size: "sm", index: currentCategory, onChange: setCurrentCategory, display: "flex", flexDirection: "column", h: "100%", children: [_jsxs(TabList, { mb: ["1em", "1em", "1.5em"], px: [4, 4, 8], gap: 4, overflowX: "auto", flexShrink: 0, justifyContent: ["flex-start", "flex-start", "center"], children: [hasVibes && (_jsx(Tab, { color: "white", bg: "#1E1E1E", border: "1px solid", borderColor: "#3E3E3E", borderRadius: 12, fontSize: ["xs", "xs", "sm"], fontWeight: "400", whiteSpace: "nowrap", _selected: {
                                        color: "black",
                                        bg: "white",
                                        borderColor: "white",
                                    }, children: "Chillout Moments" })), tableLayout.length > 0 && (_jsx(Tab, { color: "white", bg: "#1E1E1E", border: "1px solid", borderColor: "#3E3E3E", borderRadius: 12, fontSize: ["xs", "xs", "sm"], fontWeight: "400", whiteSpace: "nowrap", _selected: {
                                        color: "black",
                                        bg: "white",
                                        borderColor: "white",
                                    }, children: "Table Layout" })), _jsx(Tab, { color: "white", bg: "#1E1E1E", border: "1px solid", borderColor: "#3E3E3E", borderRadius: 12, fontSize: ["xs", "xs", "sm"], fontWeight: "400", whiteSpace: "nowrap", _selected: {
                                        color: "black",
                                        bg: "white",
                                        borderColor: "white",
                                    }, children: "Venue Images" })] }), _jsxs(TabPanels, { flex: 1, display: "flex", flexDirection: "column", minH: 0, children: [hasVibes && (_jsx(TabPanel, { pt: [0, 0, 4], px: [0, 0, 4], flex: 1, display: "flex", flexDirection: "column", minH: 0, children: renderGalleryContent(vibes.map((src) => ({
                                        src,
                                        type: isVideo(src) ? "video" : "image",
                                    }))) })), tableLayout.length > 0 && (_jsx(TabPanel, { pt: [0, 0, 4], px: [0, 0, 4], flex: 1, display: "flex", flexDirection: "column", minH: 0, children: renderGalleryContent(tableLayout.map((item) => ({
                                        src: item.imageUrl,
                                        type: isVideo(item.imageUrl) ? "video" : "image",
                                        floorName: item.floorName,
                                        areaName: item.areaName,
                                    }))) })), _jsx(TabPanel, { pt: [0, 0, 4], px: [0, 0, 4], flex: 1, display: "flex", flexDirection: "column", minH: 0, children: renderGalleryContent(images.map((src) => ({
                                        src,
                                        type: isVideo(src) ? "video" : "image",
                                    }))) })] })] }) })] }));
};
export default PhotoGallery;
